<template>
  <!-- Non-dismissable halt banner. Renders only while the portfolio gate has fired. -->
  <div
    v-if="show"
    class="alert d-flex align-items-start gap-2 shadow-sm mb-4 dd-guard-banner"
    :class="isLoss ? 'alert-danger' : 'alert-success'"
    role="alert"
    aria-live="assertive"
  >
    <i class="fas mt-1 flex-shrink-0" :class="isLoss ? 'fa-triangle-exclamation' : 'fa-circle-check'"></i>
    <div class="flex-grow-1 min-w-0">
      <div class="fw-bold">{{ headline }}</div>
      <div class="small">
        Day P&amp;L <span class="fw-semibold">{{ pnlText }}</span>;
        {{ bodyText }}<template v-if="triggeredAtText"> at
        <span class="fw-semibold">{{ triggeredAtText }}</span></template>. Resumes next session.
      </div>
      <div v-if="flattened.length" class="small mt-1">
        Open at trigger:
        <span
          v-for="t in flattened"
          :key="t"
          class="badge me-1"
          :class="isLoss ? 'bg-danger-subtle text-danger border border-danger-subtle' : 'bg-success-subtle text-success border border-success-subtle'"
        >{{ t }}</span>
        <span class="text-muted">— confirm against Positions below.</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DrawdownGuardBanner',
  props: {
    guard: { type: Object, default: null },
  },
  computed: {
    show() {
      return !!(this.guard && this.guard.enabled && this.guard.triggered);
    },
    isLoss() {
      return this.guard?.reason === 'LOSS_STOP';
    },
    headline() {
      return this.isLoss
        ? 'Trading halted — daily loss limit hit.'
        : 'Trading halted — daily profit target reached.';
    },
    // The gate raises a signal; each position's watcher performs the actual
    // close through its own force-close path. `flattened_tickers` is therefore
    // the set of positions OPEN when the gate fired — its close *intent*, not a
    // record of confirmed closes. Wording must not assert an outcome the gate
    // has no knowledge of; the Positions table below is the source of truth for
    // what actually closed.
    bodyText() {
      return this.isLoss
        ? 'new entries blocked and all positions signalled to close'
        : 'new entries blocked and positions signalled to close (ratchet leaders keep running)';
    },
    pnlText() {
      const pct = Number(this.guard?.day_pnl_pct);
      if (!Number.isFinite(pct)) return '—';
      const sign = pct > 0 ? '+' : '';
      return `${sign}${(pct * 100).toFixed(2)}%`;
    },
    triggeredAtText() {
      return this.formatTime(this.guard?.triggered_at);
    },
    flattened() {
      const list = this.guard?.flattened_tickers;
      return Array.isArray(list) ? list : [];
    },
  },
  methods: {
    formatTime(value) {
      if (!value) return '';
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.dd-guard-banner {
  border-left: 4px solid var(--bs-secondary, #6c757d);
}
.min-w-0 {
  min-width: 0;
}
</style>
